// GENERADO: editar packages/backend/src/shared/quote-metrics.ts y ejecutar node scripts/sync-quote-metrics.mjs
/** Fuente compartida del cotizador. La copia del frontend se genera mediante
 * scripts/sync-quote-metrics.mjs y CI comprueba que no diverja. Sin dependencias
 * de servidor: sólo interpreta los resultados del evaluador de fórmulas. */
export interface MetricVariable {
  code: string;
  priceRole?: string | null;
  formula?: string | null;
  isGlobal?: boolean;
  variableType?: string;
}
export interface MetricLine {
  quantity: number;
  costInputs?: Record<string, unknown>;
  calculations?: Record<string, unknown> | null;
}
const role = (variables: MetricVariable[], name: string) =>
  variables.find((v) => v.priceRole === name)?.code;
const numeric = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
};

/** ROUND_HALF_UP sobre la representación decimal, también para 1.005 y
 * notación exponencial. Evita Math.round(value * 100) y sus errores binarios. */
export function roundAmount(value: number): number {
  if (!Number.isFinite(value)) return value;
  const [mantissa, exponent = '0'] = Math.abs(value).toString().split('e');
  const [whole, fraction = ''] = mantissa.split('.');
  const coefficient = BigInt(whole + fraction);
  const scale = fraction.length - Number(exponent) - 2;
  const factor = BigInt(10) ** BigInt(Math.abs(scale));
  const cents = scale > 0 ? (coefficient + factor / BigInt(2)) / factor : coefficient * factor;
  return (Number(cents) / 100) * (value < 0 ? -1 : 1);
}

export function linePrices(
  variables: MetricVariable[],
  calculations: Record<string, unknown>,
  quantity: number
) {
  const unitRole = role(variables, 'UNIT_PRICE');
  const totalRole = role(variables, 'TOTAL_PRICE');
  const unitNames = [
    'PRECIO_FINAL_UNITARIO',
    'PRECIO_UNITARIO',
    'PRECIO_CON_BOLETA',
    'SUBTOTAL_SIN_IVA',
    'BASE_COSTO_UNITARIO',
  ];
  const totalNames = [
    'PRECIO_FINAL_TOTAL',
    'PRECIO_TOTAL',
    'TOTAL_CON_BOLETA',
    'TOTAL_FACTURADO_SIN_BOLETA',
    'COSTO_TOTAL',
    'TOTAL',
  ];
  const unitCode = unitRole ?? Object.keys(calculations).find((code) => unitNames.includes(code));
  const totalCode =
    totalRole ?? Object.keys(calculations).find((code) => totalNames.includes(code));
  const unit = unitCode ? numeric(calculations[unitCode]) : null;
  const total = totalCode ? numeric(calculations[totalCode]) : null;
  // Precedencia vigente del panel: UNIT_PRICE gana cuando existen ambos roles.
  if (unit !== null)
    return { unitPrice: unit, totalPrice: roundAmount(unit * quantity), source: unitCode! };
  if (total !== null)
    return {
      unitPrice: quantity > 0 ? total / quantity : 0,
      totalPrice: roundAmount(total),
      source: totalCode!,
    };
  const fallback = variables
    .filter(
      (v) => !v.formula && !v.isGlobal && v.code !== 'CANTIDAD' && v.variableType === 'CURRENCY'
    )
    .reduce((sum, v) => sum + (numeric(calculations[v.code]) ?? 0), 0);
  return {
    unitPrice: fallback,
    totalPrice: roundAmount(fallback * quantity),
    source: 'legacy_currency_sum',
  };
}

export function lineCost(line: MetricLine, variables: MetricVariable[]): number | null {
  const qty = Number(line.quantity) || 1;
  const calc = line.calculations ?? {},
    inputs = line.costInputs ?? {};
  const base = role(variables, 'COST_BASE_MARGIN');
  const unit = role(variables, 'COST_INPUT') ?? role(variables, 'UNIT_COST');
  // Cero declarado es un costo conocido. Un valor ausente nunca se vuelve cero.
  for (const [value, multiplier] of [
    [base ? (calc[base] ?? inputs[base]) : undefined, 1],
    [unit ? (calc[unit] ?? inputs[unit]) : undefined, qty],
    [calc.COSTO_TOTAL ?? calc.COST_TOTAL ?? calc.BASE_COST, 1],
    [calc.COSTO_UNITARIO ?? calc.COST_UNIT ?? calc.UNIT_COST, qty],
    [
      inputs.COSTO_ALMACEN ??
        inputs.COSTO_UNITARIO ??
        inputs.COSTO ??
        inputs.VALOR_FOB ??
        inputs.FOB,
      qty,
    ],
  ] as Array<[unknown, number]>) {
    const number = numeric(value);
    if (number !== null && number >= 0) return roundAmount(number * multiplier);
  }
  return null;
}

export function lineMarginAmount(line: MetricLine, variables: MetricVariable[]): number | null {
  const calc = line.calculations ?? {};
  const code = role(variables, 'MARGIN');
  for (const value of [
    code ? calc[code] : undefined,
    calc.MONTO_MARGEN,
    calc.MARGEN_MONTO,
    calc.MARGIN_AMOUNT,
    calc.GANANCIA,
    calc.UTILIDAD,
  ]) {
    const number = numeric(value);
    if (number !== null) return roundAmount(number);
  }
  return null;
}

export function quoteMargin(
  baseCost: number | null,
  modelMargin: number | null,
  finalPrice: number
) {
  if (baseCost === null)
    return { percentage: null, basis: 'unavailable' as const, source: 'missing_cost' };
  if (baseCost <= 0)
    return { percentage: 0, basis: 'cost' as const, source: 'zero_cost_panel_convention' };
  if (modelMargin !== null)
    return {
      percentage: (modelMargin / baseCost) * 100,
      basis: 'cost' as const,
      source: 'model_margin_amount',
    };
  return {
    percentage: finalPrice <= 0 ? 0 : ((finalPrice - baseCost) / finalPrice) * 100,
    basis: 'revenue' as const,
    source: 'price_minus_cost',
  };
}
